import {
  kachchhPinByNormalizedName,
  kachchhPostOffices,
} from '../data/master/kachchhPins';
import { normalizeVillageName } from '../data/master/villages';

function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Resolve PIN for a Kachchh village / locality.
 * 1) Exact post-office name match
 * 2) Starts-with / contains match
 * 3) Nearest post office to lat/lng (within ~8 km)
 */
export function lookupPinForVillage(villageName, { latitude, longitude } = {}) {
  const n = normalizeVillageName(villageName);
  if (!n) return null;

  if (kachchhPinByNormalizedName[n]) {
    return kachchhPinByNormalizedName[n];
  }

  // Soft match — office name contains village or vice versa
  let soft = null;
  for (const [key, pin] of Object.entries(kachchhPinByNormalizedName)) {
    if (key.startsWith(n) || n.startsWith(key)) {
      soft = pin;
      break;
    }
  }
  if (soft) return soft;

  if (latitude != null && longitude != null) {
    let best = null;
    let bestKm = Infinity;
    for (const o of kachchhPostOffices) {
      const km = haversineKm(latitude, longitude, o.lat, o.lng);
      if (km < bestKm) {
        bestKm = km;
        best = o.pin;
      }
    }
    if (best && bestKm <= 8) return best;
  }

  return null;
}

/**
 * Live India Post API fallback (when local directory has no match).
 * Prefers Gujarat / Kachchh results when present, else first hit.
 * Returns PIN only (legacy callers).
 */
export async function fetchPinFromIndiaPost(villageName) {
  const info = await fetchPostOfficeFromIndiaPostByName(villageName);
  return info?.pin || null;
}

/**
 * Resolve district/state/PIN from India Post by post-office / place name.
 * Works for any district in India (not Kachchh-only).
 */
export async function fetchPostOfficeFromIndiaPostByName(villageName) {
  const q = encodeURIComponent(String(villageName || '').trim());
  if (!q) return null;

  const res = await fetch(`https://api.postalpincode.in/postoffice/${q}`);
  if (!res.ok) return null;
  const data = await res.json();
  const offices = data?.[0]?.PostOffice;
  if (!offices?.length) return null;

  const kachchh = offices.find(
    (o) =>
      /kachchh|kutch/i.test(o.District || '') &&
      /gujarat/i.test(o.State || ''),
  );
  const gujarat = offices.find((o) => /gujarat/i.test(o.State || ''));
  const hit = kachchh || gujarat || offices[0];
  return officeToLocation(hit);
}

/**
 * Resolve district/state from India Post by PIN (any Indian PIN).
 * Prefer this when autofill already has a PIN but no district.
 */
export async function fetchLocationFromIndiaPostPin(pin) {
  const p = String(pin || '').trim();
  if (!/^\d{6}$/.test(p)) return null;

  const res = await fetch(`https://api.postalpincode.in/pincode/${encodeURIComponent(p)}`);
  if (!res.ok) return null;
  const data = await res.json();
  const offices = data?.[0]?.PostOffice;
  if (!offices?.length) return null;
  return officeToLocation(offices[0]);
}

function officeToLocation(hit) {
  if (!hit) return null;
  return {
    pin: hit.Pincode ? String(hit.Pincode) : null,
    district: hit.District ? String(hit.District).trim() : null,
    state: hit.State ? String(hit.State).trim() : null,
    country: 'India',
    taluka: hit.Block ? String(hit.Block).trim() : null,
  };
}
